/**
 * Connect4 — in-chat 1v1 board UI.
 *
 * Faithful to the legacy SWF's `/play_connect4` mini-game: a 7×6 grid,
 * click a column to drop, first to 4-in-a-row wins. Server is authoritative
 * (move validation, win detection, persistence to prochat_games).
 *
 * Two surfaces:
 *   - <C4InviteBanner> — pending invite (incoming or outgoing) at the top
 *     of the chat area; accept/decline or "waiting…" with a TTL countdown.
 *   - <C4Board> — the live game; cells coloured red (player 0) / yellow
 *     (player 1), turn indicator, resign button, end-of-game banner.
 */
import { useEffect, useState } from 'react';
import type { C4Active, C4Pending } from '../hooks/useChat.js';

const COLS = 7;
const ROWS = 6;

// ---------- countdown helper -----------------------------------------------
function useCountdown(deadline: number): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 500);
        return () => clearInterval(id);
    }, []);
    return Math.max(0, Math.ceil((deadline - now) / 1000));
}

// ---------- invite banner ---------------------------------------------------
export function C4InviteBanner(props: {
    pending: C4Pending;
    onAccept: () => void;
    onDecline: () => void;
}) {
    const { pending, onAccept, onDecline } = props;
    const secs = useCountdown(pending.deadline);

    if (pending.kind === 'invite_sent') {
        return (
            <div className="c4-banner c4-banner-sent">
                <span className="c4-banner-text">
                    Waiting for <strong style={{ color: pending.other.color }}>{pending.other.name}</strong> to accept Connect 4… ({secs}s)
                </span>
                <button className="c4-btn c4-btn-decline" onClick={onDecline}>Cancel</button>
            </div>
        );
    }

    return (
        <div className="c4-banner c4-banner-recv">
            <span className="c4-banner-text">
                <strong style={{ color: pending.other.color }}>{pending.other.name}</strong> challenges you to Connect 4! ({secs}s)
            </span>
            <button className="c4-btn c4-btn-accept"  onClick={onAccept}>Accept</button>
            <button className="c4-btn c4-btn-decline" onClick={onDecline}>Decline</button>
        </div>
    );
}

// ---------- board -----------------------------------------------------------
export function C4Board(props: {
    game: C4Active;
    onMove: (col: number) => void;
    onResign: () => void;
    onDismiss: () => void;
    onRematch: () => void;
}) {
    const { game, onMove, onResign, onDismiss, onRematch } = props;
    const spectator = game.you_play === null;
    const yourTurn = !spectator && game.status === 'playing' && game.turn === game.you_play;
    const turnName = game.players[game.turn] || '???';
    const opponent = spectator ? '' : (game.players[1 - (game.you_play as 0 | 1)] || '???');

    // Status line
    let status: React.ReactNode;
    if (game.status === 'ended') {
        if (game.reason === 'draw') status = <span className="c4-status c4-draw">Draw — board full.</span>;
        else if (spectator) {
            const winnerName = game.winner !== null ? (game.players[game.winner] || '???') : '???';
            status = <span className="c4-status c4-spectate">{winnerName} won{game.reason === 'resigned' ? ' (resignation)' : '.'}</span>;
        }
        else if (game.winner === game.you_play) {
            status = <span className="c4-status c4-win">You win{game.reason === 'resigned' ? ` — ${opponent} resigned.` : '!'} 🏆</span>;
        } else {
            status = <span className="c4-status c4-loss">You lose{game.reason === 'resigned' ? ' (you resigned)' : ` — ${opponent} won.`}</span>;
        }
    } else if (spectator) {
        status = <span className="c4-status c4-spectate">{turnName}'s turn ({game.turn === 0 ? '🔴' : '🟡'})</span>;
    } else {
        status = yourTurn
            ? <span className="c4-status c4-yourturn">Your move.</span>
            : <span className="c4-status c4-theirturn">Waiting for {opponent}…</span>;
    }

    // Determine the lowest empty row per column for hover preview (top-only click hint).
    function lowestEmpty(col: number): number {
        for (let r = ROWS - 1; r >= 0; r--) if (game.board[r]![col] === 0) return r;
        return -1;
    }

    let header: React.ReactNode;
    if (spectator) {
        header = (
            <span className="c4-vs">
                Watching Connect 4: <strong>{game.players[0]}</strong> 🔴 vs <strong>{game.players[1]}</strong> 🟡
            </span>
        );
    } else {
        const yp = game.you_play as 0 | 1;
        header = (
            <span className="c4-vs">
                Connect 4 — you ({yp === 0 ? '🔴 red' : '🟡 yellow'}) vs {opponent}
            </span>
        );
    }

    return (
        <div className={`c4-wrap ${game.status === 'ended' ? 'c4-ended' : ''} ${spectator ? 'c4-spectator' : ''}`}>
            <div className="c4-head">
                {header}
                {status}
                {game.status === 'playing' && !spectator
                    && <button className="c4-btn c4-btn-decline" onClick={onResign}>Resign</button>}
                {game.status === 'ended' && !spectator
                    && <button className="c4-btn c4-btn-accept" onClick={onRematch} title="Challenge the same opponent to a new game">Rematch</button>}
                {game.status === 'ended'
                    && <button className="c4-btn c4-btn-decline" onClick={onDismiss}>Close</button>}
            </div>
            <div className="c4-grid" role="grid" aria-label="Connect 4 board">
                {Array.from({ length: ROWS }).map((_, r) => (
                    <div key={r} className="c4-row" role="row">
                        {Array.from({ length: COLS }).map((__, c) => {
                            const v = game.board[r]?.[c] ?? 0;
                            const empty = v === 0;
                            const dropRow = lowestEmpty(c);
                            const clickable = yourTurn && dropRow >= 0;
                            return (
                                <div
                                    key={c}
                                    role="gridcell"
                                    className={`c4-cell ${empty ? 'c4-empty' : v === 1 ? 'c4-p0' : 'c4-p1'} ${clickable ? 'c4-clickable' : ''}`}
                                    onClick={clickable ? () => onMove(c) : undefined}
                                    aria-label={`col ${c + 1} row ${r + 1}${empty ? ' empty' : v === 1 ? ' red' : ' yellow'}`}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}
